<template>
  <TransitionRoot as="template" :show="precheckOpen">
    <Dialog as="div" class="fixed z-10 inset-0 overflow-y-auto" @close="closeModal">
      <div
        class="flex items-end justify-center min-h-screen px-4 pb-20 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              inline-block
              align-bottom
              bg-white
              rounded-3xl
              px-6
              py-4
              2xl:py-8
              md:absolute
              md:top-1/2
              md:left-1/2
              transform
              md:-translate-x-1/2 md:-translate-y-1/2
              text-left
              overflow-hidden
              shadow-xl
              transition-all
              w-full
              sm:max-w-4xl
              sm:w-full
            "
          >
            <div>
              <div class="inline-flex w-full justify-between">
                <h2 class="mb-1 2xl:mb-4 text-xl leading-7 font-bold text-vice-teal-900">
                  Before we start...
                </h2>
                <CrossGrayIcon class="h-7 w-7 cursor-pointer" @click="closeModal" />
              </div>
              <div>
                <div class="border-b pb-1 2xl:pb-6 border-gray-200">
                  <ul class="list-disc mx-5">
                    <li>
                      <span class="text-md font-medium text-gray-900 mt-1">
                        Make sure your browser has permission to use your camera and microphone.
                      </span>
                    </li>
                    <li>
                      <span class="text-md font-medium text-gray-900 mt-1">
                        Close any other apps or tabs that might be using your camera or mic.
                      </span>
                    </li>
                    <li>
                      <span class="text-md font-medium text-gray-900 mt-1">
                        If you're on a laptop, keep it plugged in.
                      </span>
                    </li>
                  </ul>
                </div>
                <div class="flex mt-4">
                  <p class="mb-4 text-lg leading-3 2xl:leading-10 font-bold text-vice-teal-900">
                    <span
                      class="
                        inline-block
                        h-7
                        w-7
                        mr-4
                        rounded-full
                        text-sm text-center
                        leading-7
                        bg-vice-teal-400
                        text-white
                      "
                    >
                      1
                    </span>
                    Check platform requirements
                  </p>
                </div>
                <div class="flex flex-col md:flex-row gap-1 w-full">
                  <div
                    role="list"
                    class="
                      divide-y divide-gray-300
                      border
                      px-3
                      py-1
                      2xl:py-3
                      rounded-l-lg
                      w-full
                      md:w-1/2
                      bg-vice-easy-grey
                    "
                  >
                    <div class="container inline-block justify-content-center mt-2 mb-2">
                      <CameraAndMicroPrecheck
                        v-if="permissionsGranted"
                        @precheckPassed="isPrecheckPassed = true"
                      />
                      <PermissionsOverlay v-else :permissionsStatus="permissionsStatus" />
                    </div>
                  </div>

                  <div
                    role="list"
                    class="border px-3 py-1 2xl:py-3 w-full md:w-1/2 rounded-r-lg bg-vice-easy-grey"
                  >
                    <div
                      class="
                        w-full
                        inline-flex
                        font-semibold
                        text-md text-gray-900
                        justify-between
                        pt-2
                        pb-5
                      "
                    >
                      <span>Additional requirements:</span>
                    </div>
                    <ConnectionPrecheck :isClinician="false" />
                  </div>
                </div>
              </div>
              <div class="flex justify-center">
                <span class="text-md font-medium text-gray-900 mt-3">
                  If you have any issues, please contact
                  <span class="underline cursor-pointer" @click="openIntercom">support</span>
                </span>
              </div>
              <div class="flex justify-center mt-1 2xl:mt-5 border-b pb-1 2xl:pb-6 border-gray-200">
                <ViceButton
                  data-cy="entry-start-session"
                  @click="openSessionEntryWizard"
                  ref="continueWizardRef"
                  :class="[
                    { 'pointer-events-none opacity-60': !isPrecheckPassed },
                    { 'sm:bg-gray-150 sm:text-black': !isPrecheckPassed },
                  ]"
                  class="cursor-pointer step-confirm-start-session px-7"
                  :loading="false"
                  red
                >
                  Continue
                  <span class="arrow-down">▾</span>
                </ViceButton>
              </div>
              <div class="flex mt-4">
                <p class="text-lg leading-10 font-bold text-vice-teal-900">
                  <span
                    class="
                      inline-block
                      h-7
                      w-7
                      mr-5
                      rounded-full
                      text-sm text-center
                      leading-7
                      bg-gray-500
                      text-white
                    "
                  >
                    2
                  </span>
                  Review professional standards
                </p>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { DialogOverlay, Dialog, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { onUnmounted, ref } from 'vue';
import { show } from '@intercom/messenger-js-sdk';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import CrossGrayIcon from '@/Components/Icons/CrossGrayIcon.vue';
import CameraAndMicroPrecheck from '@/Components/Devices/CameraAndMicroPrecheck.vue';
import ConnectionPrecheck from '@/Components/Devices/ConnectionPrecheck.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import PermissionsOverlay from '@/Components/Devices/PermissionsOverlay.vue';

export default {
  name: 'SessionEntryPrecheckWizard',
  props: {
    precheckOpen: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    CrossGrayIcon,
    CameraAndMicroPrecheck,
    Dialog,
    ViceButton,
    DialogOverlay,
    TransitionRoot,
    TransitionChild,
    PermissionsOverlay,
    ConnectionPrecheck,
  },
  emits: ['closeSessionEntryPrecheckWizard', 'openSessionEntryWizard'],
  setup(props, { emit }) {
    const isPrecheckPassed = ref(false);
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const permissionsGranted = ref(false);
    const permissionsStatus = ref({ camera: null, microphone: null });

    const checkAndUpdatePermissions = async () => {
      try {
        const permissions = await multiCameraDevicesStore.checkPermissions();
        permissionsStatus.value = permissions;
        permissionsGranted.value = !Object.values(permissions).includes(false);
        if (!permissionsGranted.value) {
          isPrecheckPassed.value = false;
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
        permissionsGranted.value = false;
        isPrecheckPassed.value = false;
      }
    };

    const permissionCheckInterval = setInterval(checkAndUpdatePermissions, 2000);
    onUnmounted(() => clearInterval(permissionCheckInterval));

    const closeModal = () => {
      isPrecheckPassed.value = false;
      emit('closeSessionEntryPrecheckWizard');
    };

    const openSessionEntryWizard = () => {
      emit('openSessionEntryWizard');
      closeModal();
    };

    const openIntercom = () => {
      show();
    };
    return {
      isPrecheckPassed,
      permissionsStatus,
      permissionsGranted,
      closeModal,
      openIntercom,
      openSessionEntryWizard,
    };
  },
};
</script>

<style scoped>
.arrow-down {
  font-family: 'Segoe UI Symbol', serif;
  font-size: 1rem;
}
</style>
